import React, { useEffect, useRef, useMemo } from 'react';
import * as Cesium from 'cesium';
import { useMDCockpit } from '../../../hooks/useMDCockpit';
import { useCesiumMap } from '../../../hooks/useCesiumMap';
import { useMission } from '../../../context/MissionContext';
import { getReportUsedEntities } from '../../../lib/accessAnalysisReports';
import type { Satellite, SatGroup, AccessWindowPair, GroundStation, GeoFeature } from '../../../types/mission';
import styles from './CesiumMap.module.scss';

interface CesiumMapProps {
  cesium: ReturnType<typeof useCesiumMap>;
}

const CesiumMap: React.FC<CesiumMapProps> = ({ cesium }) => {
  const divRef = useRef<HTMLDivElement>(null);
  const secondaryRef = useRef<HTMLDivElement>(null);
  const {
    layers, groundStations, geoFeatures, viewMode, selectedItemId, selectedItemType, modelData, accessWindowPairs,
    selectedReportDetail, selectedCoverageReportDetail, addSatModalOpen, orbitalEditTargetId, groupEditTargetId,
  } = useMDCockpit();
  const { satGroups, state } = useMission();
  const satellites = state.satellites;
  const initializedRef = useRef(false);
  const prevGSIds = useRef<Set<string>>(new Set());
  const prevGsFovEnabledRef = useRef<boolean | undefined>();
  const drawnGeoIds = useRef<Set<string>>(new Set());

  const activeReport = selectedReportDetail || selectedCoverageReportDetail;
  const usedEntities = useMemo(() => getReportUsedEntities(activeReport, satellites), [activeReport, satellites]);
  const isReportActive = activeReport !== null;

  const filteredSatellites = useMemo<(Satellite | SatGroup)[]>(() => {
    if (!isReportActive) return satellites;
    return satellites.map(item => {
      if ((item as SatGroup).type === 'group') {
        const grp = item as SatGroup;
        const filteredChildren = grp.children.filter(child => usedEntities.satelliteIds.has(child.id));
        if (filteredChildren.length === 0) return null;
        return { ...grp, children: filteredChildren };
      }
      const sat = item as Satellite;
      return usedEntities.satelliteIds.has(sat.id) ? sat : null;
    }).filter((x): x is Satellite | SatGroup => x !== null);
  }, [satellites, isReportActive, usedEntities]);

  const filteredSatGroups = useMemo<any[]>(() => {
    if (!isReportActive) return satGroups;
    return satGroups.map(grp => {
      const filteredSats = grp.satellites.filter((sat: any) => usedEntities.satelliteIds.has(sat.id));
      if (filteredSats.length === 0) return null;
      return { ...grp, satellites: filteredSats };
    }).filter((x): x is any => x !== null);
  }, [satGroups, isReportActive, usedEntities]);

  const filteredGroundStations = useMemo<GroundStation[]>(() => {
    if (!isReportActive) return groundStations;
    return groundStations.filter(gs => usedEntities.groundStationIds.has(gs.id));
  }, [groundStations, isReportActive, usedEntities]);

  const filteredGeoFeatures = useMemo<GeoFeature[]>(() => {
    if (!isReportActive) return geoFeatures;
    return geoFeatures.filter(gf => usedEntities.geoFeatureIds.has(gf.id));
  }, [geoFeatures, isReportActive, usedEntities]);

  const filteredAccessWindowPairs = useMemo<AccessWindowPair[]>(() => {
    if (!isReportActive) return accessWindowPairs;
    return (accessWindowPairs as AccessWindowPair[]).filter(pair => {
      const isSatUsed = usedEntities.satelliteIds.has(pair.satelliteId);
      let isCounterpartyUsed = false;
      if (pair.counterpartyKind === 'satellite') {
        isCounterpartyUsed = usedEntities.satelliteIds.has(pair.counterpartyId);
      } else if (pair.counterpartyKind === 'ground_station') {
        isCounterpartyUsed = usedEntities.groundStationIds.has(pair.counterpartyId);
      } else {
        isCounterpartyUsed = usedEntities.geoFeatureIds.has(pair.counterpartyId);
      }
      return isSatUsed && isCounterpartyUsed;
    });
  }, [accessWindowPairs, isReportActive, usedEntities]);

  const getSatsWithSensors = (): Satellite[] => filteredSatellites.flatMap((item) => {
    if ((item as SatGroup).type === 'group') {
      return (item as SatGroup).children.filter((c) => (c.sensors?.length ?? 0) > 0);
    }
    return (item as Satellite).sensors?.length ? [item as Satellite] : [];
  });

  // Mount: init primary viewer
  useEffect(() => {
    if (!divRef.current || initializedRef.current) return;
    initializedRef.current = true;
    cesium.initViewer(divRef.current);
    geoFeatures.forEach((gf) => {
      drawnGeoIds.current.add(gf.id);
      cesium.setGeoFeatureVisible(gf.id, gf.active);
      cesium.commitGeoFeature(gf);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Double-click an object on the globe to focus it without opening detail panels.
  useEffect(() => {
    const viewer = cesium.viewerRef.current;
    if (!viewer) return;

    const satelliteIds = new Set<string>();
    filteredSatellites.forEach((item) => {
      if ((item as SatGroup).type === 'group') {
        (item as SatGroup).children.forEach(child => satelliteIds.add(child.id));
      } else {
        satelliteIds.add((item as Satellite).id);
      }
    });
    const groundStationByPointId = new Map(filteredGroundStations.map(gs => [`gs_point_${gs.id}`, gs]));
    const geoFeatureById = new Map(filteredGeoFeatures.flatMap(gf => [[gf.id, gf], [`${gf.id}_outline`, gf]] as const));

    const getGeoFeatureCenter = (geoId: string) => {
      const geo = geoFeatureById.get(geoId);
      if (!geo) return null;
      if (geo.kind === 'point' && geo.lat != null && geo.lng != null) return { lon: geo.lng, lat: geo.lat };
      if (geo.kind === 'circle' && geo.centerLat != null && geo.centerLng != null) return { lon: geo.centerLng, lat: geo.centerLat };
      if (geo.coordinates?.length) {
        const sum = geo.coordinates.reduce((acc, point) => ({
          lat: acc.lat + point.lat,
          lng: acc.lng + point.lng,
        }), { lat: 0, lng: 0 });
        return { lon: sum.lng / geo.coordinates.length, lat: sum.lat / geo.coordinates.length };
      }
      return null;
    };

    const handler = viewer.screenSpaceEventHandler;
    const previousDoubleClick = handler.getInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

    handler.setInputAction((event: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
      const picked = viewer.scene.pick(event.position);
      if (!Cesium.defined(picked) || !picked.id) return;

      const entityId = typeof picked.id === 'string' ? picked.id : picked.id.id;
      if (satelliteIds.has(entityId)) {
        cesium.highlightSatellite(entityId);
        cesium.centerOnSatellite(entityId);
        return;
      }

      const groundStation = groundStationByPointId.get(entityId);
      if (groundStation) {
        cesium.highlightGroundStation(groundStation.id);
        cesium.centerOnPoint(groundStation.lon, groundStation.lat);
        return;
      }

      const geoCenter = getGeoFeatureCenter(entityId);
      if (geoCenter) {
        cesium.setSelectedGeoFeatures([geoFeatureById.get(entityId)!.id]);
        cesium.centerOnPoint(geoCenter.lon, geoCenter.lat);
        return;
      }

      const entity = typeof picked.id === 'string' ? viewer.entities.getById(picked.id) : picked.id;
      const pos = entity?.position?.getValue(viewer.clock.currentTime);
      if (!pos) return;
      const carto = Cesium.Cartographic.fromCartesian(pos);
      cesium.centerOnPoint(Cesium.Math.toDegrees(carto.longitude), Cesium.Math.toDegrees(carto.latitude));
    }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
    return () => {
      if (previousDoubleClick) {
        handler.setInputAction(previousDoubleClick, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
      } else {
        handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredGeoFeatures, filteredGroundStations, filteredSatellites]);

  // Keep globe selection highlights in sync when the tree selection changes.
  useEffect(() => {
    cesium.setSelectedSatellite(null);
    cesium.setSelectedGroundStations([]);
    cesium.setSelectedGeoFeatures([]);

    if (selectedItemId) {
      if (selectedItemType === 'satellite') {
        cesium.setSelectedSatellite(selectedItemId);
      } else if (selectedItemType === 'group') {
        const grp = satellites.find(item => (item as SatGroup).type === 'group' && item.id === selectedItemId) as SatGroup | undefined;
        if (grp && grp.children && grp.children.length > 0) {
          cesium.setSelectedSatellite(grp.children[0].id);
        }
      } else if (selectedItemType === 'groundstation') {
        cesium.setSelectedGroundStations([selectedItemId]);
      } else if (selectedItemType === 'geofeature') {
        cesium.setSelectedGeoFeatures([selectedItemId]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemId, selectedItemType, satellites]);

  // Sync geo feature visibility when active flags change
  useEffect(() => {
    const currentIds = new Set(geoFeatures.map(gf => gf.id));
    drawnGeoIds.current.forEach(id => {
      if (!currentIds.has(id)) {
        cesium.removeGeoFeature(id);
        drawnGeoIds.current.delete(id);
      }
    });

    geoFeatures.forEach((gf) => {
      if (!drawnGeoIds.current.has(gf.id)) {
        drawnGeoIds.current.add(gf.id);
        cesium.commitGeoFeature(gf);
      }
      const isVisible = gf.active && (!isReportActive || usedEntities.geoFeatureIds.has(gf.id));
      cesium.setGeoFeatureVisible(gf.id, isVisible);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoFeatures, isReportActive, usedEntities]);

  // Sync layer visibility whenever layers change
  useEffect(() => {
    if (!layers) return;
    cesium.updateLayers(layers);

    if (prevGsFovEnabledRef.current !== layers.gsFov) {
      prevGsFovEnabledRef.current = layers.gsFov;
      filteredGroundStations.forEach(gs => cesium.addOneGroundStation(gs));
      cesium.updateLayers(layers);
    }

    if (layers.groundTracks && filteredSatGroups?.length) {
      cesium.drawGroundTracks(filteredSatGroups);
    } else {
      cesium.clearGroundTracks();
    }

    if (layers.los) {
      const focusedId = selectedItemType === 'satellite' ? selectedItemId : null;
      cesium.drawLOS(filteredSatGroups ?? [], filteredGroundStations, focusedId, filteredAccessWindowPairs, filteredGeoFeatures);
    } else {
      cesium.clearLOS();
    }

    cesium.clearSensorFovs();
    if (layers.sensorFovs) {
      const satsWithSensors = getSatsWithSensors();
      if (satsWithSensors.length) cesium.drawSensorFovs(satsWithSensors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers, filteredGroundStations, filteredSatGroups, filteredAccessWindowPairs, filteredGeoFeatures, selectedItemId, selectedItemType]);

  // Diff-sync ground stations: add new, update changed, remove deleted
  useEffect(() => {
    const currentIds = new Set(filteredGroundStations.map(gs => gs.id));

    // Remove deleted stations
    prevGSIds.current.forEach(id => {
      if (!currentIds.has(id)) {
        cesium.removeGroundStation(id);
      }
    });

    // Add new or update existing stations
    filteredGroundStations.forEach(gs => {
      if (!prevGSIds.current.has(gs.id)) {
        cesium.addOneGroundStation(gs);
      } else {
        cesium.updateOneGroundStation(gs);
      }
    });

    prevGSIds.current = currentIds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredGroundStations]);

  const prevSatGroupsKeyRef = useRef<string>('');
  const prevPropWindowRef = useRef<string>('');

  // Reset key ref and clear highlights when modal opens/closes
  useEffect(() => {
    prevSatGroupsKeyRef.current = '';
    if (addSatModalOpen) {
      cesium.highlightSatellite(null); // Clear any highlight
    }
  }, [addSatModalOpen, cesium]);


  // Hide only the edited satellite orbit (or all in edited group) using visibility callbacks
  useEffect(() => {
    const ids = new Set<string>();
    if (addSatModalOpen && (orbitalEditTargetId || groupEditTargetId)) {
      if (orbitalEditTargetId) {
        ids.add(orbitalEditTargetId);
      } else if (groupEditTargetId) {
        satellites.forEach((item: any) => {
          if ((item as SatGroup).type === 'group') {
            const grp = item as SatGroup;
            if (grp.id === groupEditTargetId) {
              grp.children.forEach(child => ids.add(child.id));
            }
          }
        });
      }
    }
    cesium.setEditingSatelliteIds(ids);
  }, [addSatModalOpen, orbitalEditTargetId, groupEditTargetId, satellites, cesium]);

  // Draw real Keplerian orbits + ground tracks whenever satellite groups change
  useEffect(() => {
    const prop = modelData?.propagation as any;
    const currentPropWindow = `${prop?.startEpoch ?? ''}:${prop?.endEpoch ?? ''}`;
    const currentSatGroupsKey = (filteredSatGroups ?? [])
      .map(g => {
        const satKeys = (g.satellites ?? []).map((s: any) => {
          const trackKey = s.backendOrbitTrack
            ? `${s.backendOrbitTrack.orbitPoints?.length ?? 0}:${s.backendOrbitTrack.updatedAt ?? ''}:${s.backendOrbitTrack.runId ?? ''}`
            : 'no-track';
          const paramKey = s.orbitParameters
            ? `${s.orbitParameters.semiMajorAxis}:${s.orbitParameters.eccentricity}:${s.orbitParameters.inclination}:${s.orbitParameters.RAAN}:${s.orbitParameters.AOP}:${s.orbitParameters.TA}:${s.orbitParameters.epoch}`
            : 'no-param';
          const tleKey = s.tleLines
            ? `${s.tleLines.line1}:${s.tleLines.line2}`
            : 'no-tle';
          return `${s.id}:${trackKey}:${paramKey}:${tleKey}:${s.propagationStatus ?? ''}:${s.orbitColour ?? ''}`;
        }).join(',');
        return `${g.groupId || g.id || ''}:${satKeys}`;
      })
      .join('|') + `||${currentPropWindow}`;

    if (prop?.startEpoch && prop?.endEpoch && prevPropWindowRef.current !== currentPropWindow) {
      prevPropWindowRef.current = currentPropWindow;
      cesium.initClockFromModel(prop.startEpoch, prop.endEpoch);
    }

    if (prevSatGroupsKeyRef.current !== currentSatGroupsKey) {
      prevSatGroupsKeyRef.current = currentSatGroupsKey;
      if (filteredSatGroups?.length) {
        cesium.drawSatelliteGroupOrbits(filteredSatGroups);
        if (layers.groundTracks) cesium.drawGroundTracks(filteredSatGroups);
        else cesium.clearGroundTracks();
      } else {
        cesium.clearGroup('satellite-orbits');
        cesium.clearGroundTracks();
        cesium.clearLOS();
        cesium.clearSensorFovs();
      }
    }

    if (layers.los) cesium.drawLOS(filteredSatGroups ?? [], filteredGroundStations, null, filteredAccessWindowPairs, filteredGeoFeatures);
    else cesium.clearLOS();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSatGroups, filteredGroundStations, filteredAccessWindowPairs, filteredGeoFeatures, modelData?.propagation, layers.los, layers.groundTracks, addSatModalOpen]);

  // Rebuild LOS whenever ground stations change
  useEffect(() => {
    if (layers.los) cesium.drawLOS(filteredSatGroups ?? [], filteredGroundStations, null, filteredAccessWindowPairs, filteredGeoFeatures);
    else cesium.clearLOS();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredGroundStations, filteredSatGroups, filteredAccessWindowPairs, filteredGeoFeatures, layers.los]);

  // When a satellite is selected, narrow LOS to only that satellite's connections
  // Use a ref to track previous accessWindowPairs to avoid excessive redraws
  const prevAccessWindowsRef = useRef<AccessWindowPair[] | undefined>();

  useEffect(() => {
    const focusedId = selectedItemType === 'satellite' ? selectedItemId : null;

    // Only redraw if accessWindowPairs actually changed (not just reference equality)
    const windowsChanged = JSON.stringify(filteredAccessWindowPairs) !== JSON.stringify(prevAccessWindowsRef.current);

    if (layers.los) {
      cesium.drawLOS(filteredSatGroups ?? [], filteredGroundStations, focusedId, filteredAccessWindowPairs, filteredGeoFeatures);
      if (windowsChanged) {
        prevAccessWindowsRef.current = filteredAccessWindowPairs;
      }
    } else {
      cesium.clearLOS();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemId, selectedItemType, filteredGeoFeatures, filteredSatGroups, filteredGroundStations, filteredAccessWindowPairs, layers.los]);

  // Split view: init / destroy secondary 2D viewer
  useEffect(() => {
    if (viewMode === 'split' && secondaryRef.current) {
      cesium.initSecondaryViewer(secondaryRef.current);
    } else {
      cesium.destroySecondaryViewer();
    }
    cesium.updateLayers(layers);
    if (layers.gsFov) {
      filteredGroundStations.forEach(gs => cesium.addOneGroundStation(gs));
      cesium.updateLayers(layers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, filteredGroundStations, layers]);

  // Resize primary and secondary viewers when container size changes (e.g. split view toggles, sidebar collapses, timeline height changes)
  useEffect(() => {
    const viewer = cesium.viewerRef.current;
    if (!viewer || !divRef.current) return;

    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        if (cesium.viewerRef.current) {
          cesium.viewerRef.current.resize();
        }
      });
    });
    observer.observe(divRef.current);

    return () => {
      observer.disconnect();
    };
  }, [cesium.viewerRef]);

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        if (cesium.secondaryViewerRef.current && !cesium.secondaryViewerRef.current.isDestroyed()) {
          cesium.secondaryViewerRef.current.resize();
        }
      });
    });

    if (secondaryRef.current) {
      observer.observe(secondaryRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [cesium.secondaryViewerRef, viewMode]);

  // Redraw draft ground station on viewMode layout changes
  useEffect(() => {
    cesium.redrawDraftGroundStation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Redraw sensor FOV footprints whenever satellite list changes
  useEffect(() => {
    cesium.clearSensorFovs();
    if (layers.sensorFovs) {
      const satsWithSensors = getSatsWithSensors();
      if (satsWithSensors.length) cesium.drawSensorFovs(satsWithSensors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSatellites, layers.sensorFovs]);

  // Keep ground stations enabled and show rings only during contact
  const prevActiveGsRef = useRef<string>('');

  useEffect(() => {
    if (!filteredGroundStations?.length) return;

    // Keep ground stations enabled based on active state
    filteredGroundStations.forEach(gs => {
      cesium.setGSActive(gs.id, gs.active !== false);
    });

    const viewer = cesium.viewerRef.current;
    if (!viewer || !filteredAccessWindowPairs?.length) {
      cesium.clearAllGsRings();
      return;
    }

    // Use throttled updates - only check every 500ms to avoid animation interference
    let lastUpdateTime = 0;
    const updateInterval = 500; // ms

    const updateContactRings = () => {
      const now = Date.now();
      if (now - lastUpdateTime < updateInterval) return;
      lastUpdateTime = now;

      const currentTime = viewer.clock.currentTime;
      const currentEpochSec = Cesium.JulianDate.toDate(currentTime).getTime() / 1000;

      // Find ground stations with active contacts at current time
      const activeGsIds = new Set<string>();
      const connectionTypes = new Map<string, 'bidirectional' | 'unidirectional'>();

      filteredAccessWindowPairs.forEach(pair => {
        // Only process ground station access windows
        if (pair.counterpartyKind !== 'ground_station' || !pair.passes?.length) return;

        const gsId = pair.counterpartyId;
        const gs = filteredGroundStations.find(g => g.id === gsId);
        if (!gs || gs.active === false) return; // skip if hidden

        pair.passes.forEach(pass => {
          // Check if current time is within this pass
          if (currentEpochSec >= pass.start && currentEpochSec <= pass.end) {
            activeGsIds.add(gsId);
            connectionTypes.set(gsId, 'bidirectional');
          }
        });
      });

      // Only update if the set of active GS IDs has changed
      const activeKey = Array.from(activeGsIds).sort().join(',');
      if (activeKey !== prevActiveGsRef.current) {
        prevActiveGsRef.current = activeKey;

        // Display or clear rings based on active contacts
        if (activeGsIds.size > 0) {
          cesium.displayGsContactRings(filteredGroundStations, activeGsIds, connectionTypes);
        } else {
          cesium.clearAllGsRings();
        }
      }
    };

    // Subscribe to clock tick event
    const unsubscribe = viewer.clock.onTick.addEventListener(updateContactRings);

    return () => {
      unsubscribe();
      cesium.clearAllGsRings();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredGroundStations, filteredAccessWindowPairs]);

  const isSplit = viewMode === 'split';
  const isReportDetailOpen = selectedReportDetail !== null || selectedCoverageReportDetail !== null;

  return (
    <div
      className={styles.mapContainer}
      style={{
        right: isReportDetailOpen ? 'var(--mdai-report-panel-offset, 580px)' : '0px',
        transition: 'var(--mdai-report-panel-transition, right 0.3s ease-in-out)',
      }}
    >
      {/* Primary 3D viewer — always mounted */}
      <div style={{ flex: isSplit ? 1 : undefined, position: 'relative', height: '100%', ...(isSplit ? {} : { position: 'absolute', inset: 0 }) }}>
        <div ref={divRef} className={isSplit ? styles.mapHalf : styles.map} id="mdai-cesium-map" />
      </div>

      {isSplit && <div className={styles.splitDivider} />}

      {/* Secondary 2D viewer container — always in DOM so the Cesium viewer
          is never torn down; hidden when not in split mode to avoid GPU cost */}
      <div
        style={{
          flex: isSplit ? 1 : undefined,
          position: 'relative',
          display: isSplit ? undefined : 'none',
          height: '100%',
        }}
      >
        <div ref={secondaryRef} className={styles.mapHalf} id="mdai-cesium-map-2d" />
      </div>
    </div>
  );

};

export default CesiumMap;
